import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { FileText, Plus, X, File, Image } from 'lucide-react';

const DOCUMENT_CATEGORIES = ['Receipt', 'Manual', 'Warranty', 'Serial Photo', 'License', 'Permit', 'Other'];

// Placeholder component for documents without thumbnails
function ThumbnailPlaceholder({ fileType }) {
  const getIcon = () => {
    if (fileType?.includes('pdf')) return <FileText className="w-8 h-8 text-destructive" />;
    if (fileType?.includes('image')) return <Image className="w-8 h-8 text-secondary" />;
    return <File className="w-8 h-8 text-muted-foreground" />;
  };

  return (
    <div className="h-24 bg-muted flex flex-col items-center justify-center gap-1">
      {getIcon()}
      <span className="text-xs text-muted-foreground">No Preview</span>
    </div>
  );
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [failedThumbnails, setFailedThumbnails] = useState(new Set());

  const fetchDocuments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await api.get(`/documents?${params.toString()}`);
      setDocuments(response.data);
      setFailedThumbnails(new Set()); // Reset failed thumbnails on new fetch
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleThumbnailError = (docId) => {
    setFailedThumbnails(prev => new Set([...prev, docId]));
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout
      title="Documents"
      showSearch
      onSearch={setSearchQuery}
      searchPlaceholder="Search documents..."
    >
      <div className="space-y-6" data-testid="documents-page">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {DOCUMENT_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {categoryFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCategoryFilter('')}
                className="text-muted-foreground"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          <Button
            onClick={() => navigate('/documents/new')}
            className="bg-[#1A365D] hover:bg-[#234876] text-white rounded-sm btn-tactical"
            data-testid="add-document-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Document
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-card border border-border rounded-sm p-4 animate-pulse">
                <div className="h-24 bg-muted rounded-sm mb-4" />
                <div className="h-4 bg-muted rounded mb-2 w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Documents Found</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {categoryFilter || searchQuery
                ? 'No documents match your filters.'
                : 'Store receipts, manuals, warranties, and more.'}
            </p>
            {!categoryFilter && !searchQuery && (
              <Button
                onClick={() => navigate('/documents/new')}
                className="bg-muted hover:bg-muted/80 text-foreground rounded-sm btn-tactical"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Document
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {documents.map((doc, index) => (
              <div
                key={doc.id}
                className="bg-card border border-border rounded-sm overflow-hidden cursor-pointer card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/documents/${doc.id}`)}
                data-testid={`document-card-${doc.id}`}
              >
                {/* Preview - Show thumbnail if available, otherwise placeholder */}
                {doc.thumbnail_data && !failedThumbnails.has(doc.id) ? (
                  <div className="h-24 bg-muted">
                    <img
                      src={doc.thumbnail_data}
                      alt={doc.name}
                      className="w-full h-full object-cover"
                      onError={() => handleThumbnailError(doc.id)}
                    />
                  </div>
                ) : (
                  <ThumbnailPlaceholder fileType={doc.file_type} />
                )}

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-foreground truncate">{doc.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {doc.category}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(doc.created_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
